import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Divider } from '@heroui/react';
import { DateRangePicker } from '@enfomentis/ui';
import { PlaygroundPageLayout } from '../shared';
import type { RangeValue } from '@react-types/shared';
import type { DateValue } from '@internationalized/date';

export const DateRangePickerExamples: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState<RangeValue<DateValue> | null>(null);
  const [tradingPeriod, setTradingPeriod] = useState<RangeValue<DateValue> | null>(null);

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Basic Date Range Picker</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DateRangePicker
                label="Select Date Range"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Stay Duration"
                description="Choose your stay period"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Event Period"
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

        {/* Variants */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Variants</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateRangePicker
                label="Flat"
                variant="flat"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Bordered"
                variant="bordered"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Underlined"
                variant="underlined"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Faded"
                variant="faded"
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

        {/* Colors */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Colors</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DateRangePicker
                label="Default"
                color="default"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Primary"
                color="primary"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Secondary"
                color="secondary"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Success"
                color="success"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Warning"
                color="warning"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Danger"
                color="danger"
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

        {/* Sizes */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Sizes</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="flex flex-col gap-4">
              <DateRangePicker
                label="Small"
                size="sm"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Medium (Default)"
                size="md"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Large"
                size="lg"
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

        {/* Multiple Months */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Multiple Months Display</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateRangePicker
                label="Single Month"
                visibleMonths={1}
                className="max-w-xs"
              />
              <DateRangePicker
                label="Two Months"
                visibleMonths={2}
                className="max-w-xs"
              />
              <DateRangePicker
                label="Three Months"
                visibleMonths={3}
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

        {/* Time Granularity */}
        <Card className="max-w-7xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Time Granularity</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <DateRangePicker
                  label="Day Only"
                  granularity="day"
                  className="w-full max-w-sm"
                />
                <DateRangePicker
                  label="Hour Precision"
                  granularity="hour"
                  className="w-full max-w-md"
                />
              </div>
              <div className="space-y-4">
                <DateRangePicker
                  label="Minute Precision"
                  granularity="minute"
                  className="w-full max-w-md"
                />
                <DateRangePicker
                  label="Second Precision"
                  granularity="second"
                  className="w-full max-w-lg"
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* States */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">States</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DateRangePicker
                label="Disabled"
                isDisabled
                className="max-w-xs"
              />
              <DateRangePicker
                label="Read Only"
                isReadOnly
                className="max-w-xs"
              />
              <DateRangePicker
                label="Required"
                isRequired
                className="max-w-xs"
              />
              <DateRangePicker
                label="Invalid"
                isInvalid
                errorMessage="Please select a valid date range"
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

        {/* Trading-Specific Examples */}
        <Card className="max-w-6xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Trading Journal Use Cases</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Analysis Period Selection */}
              <div className="space-y-4">
                <h4 className="text-md font-medium">Analysis Periods</h4>
                <div className="space-y-3">
                  <DateRangePicker
                    label="Backtest Period"
                    description="Select historical data range for backtesting"
                    color="primary"
                    value={tradingPeriod}
                    onChange={setTradingPeriod}
                    className="max-w-xs"
                    visibleMonths={2}
                  />
                  <DateRangePicker
                    label="Performance Review"
                    description="Period for performance analysis"
                    color="secondary"
                    granularity="day"
                    className="max-w-xs"
                  />
                  <DateRangePicker
                    label="Strategy Comparison"
                    description="Time frame for strategy comparison"
                    variant="bordered"
                    className="max-w-xs"
                  />
                </div>
              </div>

              {/* Reporting Tools */}
              <div className="space-y-4">
                <h4 className="text-md font-medium">Reporting Tools</h4>
                <div className="space-y-3">
                  <DateRangePicker
                    label="Monthly Report"
                    description="Select month for detailed report"
                    color="success"
                    className="max-w-xs"
                  />
                  <DateRangePicker
                    label="Quarterly Analysis"
                    description="Three-month performance period"
                    color="warning"
                    visibleMonths={3}
                    className="max-w-xs"
                  />
                  <DateRangePicker
                    label="Annual Review"
                    description="Full year trading review"
                    variant="faded"
                    className="max-w-xs"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Use Cases */}
            <div className="pt-6 border-t border-border">
              <h4 className="text-md font-medium mb-3">Advanced Analysis</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DateRangePicker
                  label="Market Event Analysis"
                  description="Study periods around market events"
                  color="danger"
                  granularity="hour"
                  className="max-w-xs"
                />
                <DateRangePicker
                  label="Volatility Study Period"
                  description="High volatility period analysis"
                  variant="bordered"
                  color="primary"
                  className="max-w-xs"
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Controlled Example */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Controlled Date Range Picker</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="flex flex-col gap-4">
              <DateRangePicker
                label="Controlled Date Range"
                value={selectedRange}
                onChange={setSelectedRange}
                className="max-w-xs"
                color="primary"
                visibleMonths={2}
              />
              <div className="text-sm text-foreground-500">
                Selected range: {
                  selectedRange && selectedRange.start && selectedRange.end
                    ? `${selectedRange.start.toString()} to ${selectedRange.end.toString()}`
                    : selectedRange && selectedRange.start
                    ? `From ${selectedRange.start.toString()}`
                    : 'None'
                }
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Page Actions */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <h3 className="text-lg font-semibold">Integration Examples</h3>
          </CardHeader>
          <Divider />
          <CardBody className="space-y-4">
            <div className="space-y-4">
              <DateRangePicker
                label="Filter Trades by Date"
                description="Select date range to filter your trading history"
                color="primary"
                variant="bordered"
                className="max-w-xs"
              />
              <DateRangePicker
                label="Export Date Range"
                description="Choose period for data export"
                color="success"
                variant="flat"
                className="max-w-xs"
              />
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};